// frontend/src/components/ProviderServices/ServiceStatsCard.tsx
// Mobile-first stats component using unified StatsGrid

import React from 'react';
import { CheckCircle, XCircle, Star, BarChart3 } from 'lucide-react';
import { StatsGrid, StatItem } from '@/components/common/ui';
import { ProviderServiceStats } from '@/lib/api/providerServicesService';

interface ServiceStatsCardProps {
  stats: ProviderServiceStats;
  isLoading?: boolean;
}

const ServiceStatsCard: React.FC<ServiceStatsCardProps> = ({ stats, isLoading }) => {
  if (isLoading || !stats) {
    return (
      <div className="space-y-4">
        <StatsGrid
          stats={[]}
          isLoading={true}
          loadingItems={4}
          layout="basic"
          columns={{ mobile: 2, tablet: 2, desktop: 4 }}
        />
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm animate-pulse">
          <div className="h-4 bg-gray-200 rounded mb-3"></div>
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-8 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const statItems: StatItem[] = [
    {
      label: 'Total Services',
      value: stats.total_services || 0,
      icon: BarChart3,
      color: 'blue'
    },
    {
      label: 'Active',
      value: stats.active_services || 0,
      icon: CheckCircle,
      color: 'green'
    },
    {
      label: 'Featured',
      value: stats.featured_services || 0,
      icon: Star,
      color: 'yellow'
    },
    {
      label: 'Inactive',
      value: stats.inactive_services || 0,
      icon: XCircle,
      color: 'red'
    }
  ];

  return (
    <div className="space-y-4">
      {/* Main Stats Grid */}
      <StatsGrid
        stats={statItems}
        layout="basic"
        columns={{ mobile: 2, tablet: 2, desktop: 4 }}
      />

      {/* Category Breakdown */}
      {stats.by_category && stats.by_category.length > 0 && (
        <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            Services by Category
          </h3>
          <div className="space-y-2">
            {stats.by_category.map((category, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-700 truncate">
                  {category.category_name}
                </span>
                <span className="text-sm font-medium text-gray-900 ml-2">
                  {category.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceStatsCard;